import { useAskInputActions, useAskInputState, } from '#pplx/frontend/apps/ask/ask-input/ask-input-provider/components/AskInputProviderShared.js';
import { reportAutosuggestLatencies } from '#pplx/frontend/apps/ask/events/reportAutosuggestLatencies.js';
import { filterSortAndDedupeSuggestions, getSuggestionsLimit, isNonSequentialMatch, parseSuggestions, REPORT_LATENCY_DELAY, } from '#pplx/frontend/apps/ask/suggest-dropdown/primitives/utils/utils.js';
import { EMPTY_ARRAY } from '#pplx/frontend/common/defaults.js';
import { logger } from '#pplx/frontend/logger/browser.js';
import { useWebPlatform } from '#pplx/frontend/platform-components/useWebPlatform.js';
import { QueryClientHelper } from '#pplx/frontend/react-query/QueryClientHelper.js';
import { useQueryClient } from '@tanstack/react-query';
import { unionBy, uniqBy } from 'lodash-es';
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef } from 'react';
const KEEP_ALIVE_TIMEOUT = 10000;
const KEY = 'autosuggest';
const MALFORMED = 'AUTOSUGGEST_MESSAGE_MALFORMED'; // used for alerting and paging
const PARSE_ERROR = 'AUTOSUGGEST_MESSAGE_PARSE_ERROR'; // used for alerting and paging
const CONNECTION_ERROR = 'AUTOSUGGEST_WS_CONNECTION_ERROR';
const SEND_QUERY_ERROR = 'AUTOSUGGEST_WS_SEND_QUERY_ERROR';
const useGetCachedSuggestions = () => {
    const { device: { isMobile }, } = useWebPlatform();
    const queryClient = useQueryClient();
    return useCallback((query) => {
        const allData = queryClient.getQueryData(QueryClientHelper.makeEphemeralQueryKey(KEY));
        const queryData = queryClient.getQueryData(QueryClientHelper.makeEphemeralQueryKey(KEY, query));
        // If we have a full exact match, we're done
        if (queryData?.length && queryData.length === getSuggestionsLimit(isMobile)) {
            // These are cached in the correct order when message is received
            return queryData;
        }
        // If we don't have a full exact match, return what we can
        if (allData) {
            const compressedQuery = query.replace(/\s+/g, '').toLocaleLowerCase();
            const maxAllData = [];
            // Given allData we find matches for the current query value then
            // sort them based on how far into the Suggestion query we had to
            // search to find the entire query value
            for (const suggestion of allData) {
                if (isMobile && suggestion.url) {
                    continue;
                }
                if (suggestion.focus) {
                    // A single `finance` or `sports` focus suggestion will come from
                    // the backend for the exact query only--no non-sequential matches
                    // with focus are allowed
                    continue;
                }
                const [match, depth] = isNonSequentialMatch(compressedQuery, suggestion);
                if (match) {
                    maxAllData.push([depth, suggestion]);
                    // Never need more than limit matches as whether
                    // we're suggesting a whole list or filling in gaps the
                    // total number needed will be limit or less
                    if (maxAllData.length === getSuggestionsLimit(isMobile)) {
                        break;
                    }
                }
            }
            const maxAllDataSuggestions = maxAllData
                .sort(([depthA], [depthB]) => depthA - depthB)
                .map(([_, suggestion]) => suggestion);
            const [allSuggestions, limitedSuggestions] = filterSortAndDedupeSuggestions(maxAllDataSuggestions, isMobile, query);
            if (queryData?.length) {
                // We trust the exact match queryData to be sorted, so we just need to
                // append suggestions from allData up to a full limit; the
                // final sort will move exact matches to the front then group by url
                // and image
                const prioritizedSuggestions = [...queryData];
                for (const suggestion of allSuggestions) {
                    if (!prioritizedSuggestions.find((s) => s.query === suggestion.query)) {
                        prioritizedSuggestions.push(suggestion);
                        if (prioritizedSuggestions.length === getSuggestionsLimit(isMobile)) {
                            break;
                        }
                    }
                }
                return filterSortAndDedupeSuggestions(prioritizedSuggestions, isMobile, query)[1];
            }
            return limitedSuggestions;
        }
        return EMPTY_ARRAY;
    }, [isMobile, queryClient]);
};
export const useAutosuggest = ({ enabled = true, onSuggestionsResolved, } = {}) => {
    const { suggestions } = useAskInputState();
    const { setSuggestions } = useAskInputActions();
    const suggestionsRef = useRef(suggestions);
    suggestionsRef.current = suggestions;
    const onSuggestionsResolvedRef = useRef(onSuggestionsResolved);
    onSuggestionsResolvedRef.current = onSuggestionsResolved;
    const queryClient = useQueryClient();
    const getCachedSuggestions = useGetCachedSuggestions();
    const isAutosuggestActiveRef = useRef(false);
    const { device: { isMobile }, } = useWebPlatform();
    const socketRef = useRef(null);
    const socketKeepAliveRef = useRef(null);
    const latenciesRef = useRef([]);
    const messageToTimeMapRef = useRef({
        lastMessageID: 0,
    });
    // WS messages are runaway by design--we are waiting for a response that
    // could come at any point and if the hook is cleaned up while waiting
    // we don't want to allow an inflight message to update the suggestions
    useLayoutEffect(() => () => {
        isAutosuggestActiveRef.current = false;
    }, []);
    const maybeSetSuggestions = useCallback((query, allowEmpty = false) => {
        const autoSuggestions = getCachedSuggestions(query);
        if (!allowEmpty && !autoSuggestions.length) {
            return autoSuggestions;
        }
        if (suggestionsRef.current.length !== autoSuggestions.length ||
            unionBy(suggestionsRef.current, autoSuggestions, 'query').length !== autoSuggestions.length) {
            suggestionsRef.current = autoSuggestions;
            setSuggestions(autoSuggestions);
        }
        return autoSuggestions;
    }, [getCachedSuggestions, setSuggestions]);
    const connectWebSocket = useCallback((onOpenCallback) => {
        try {
            const socket = new WebSocket('wss://suggest.perplexity.ai/suggest/ws');
            socket.onopen = () => {
                onOpenCallback?.();
            };
            socket.onmessage = (event) => {
                // format: [query, [...suggestions], messageUuid, [...unparsedSuggestions]]
                let data;
                try {
                    data = JSON.parse(event.data);
                    if (data.length < 4) {
                        logger.error(MALFORMED, data);
                        return;
                    }
                }
                catch (error) {
                    logger.error(PARSE_ERROR, error);
                    return;
                }
                const [query, _suggestionsList, stringMessageID, unparsedSuggestions] = data;
                const messageID = parseInt(stringMessageID);
                const parsedSuggestions = parseSuggestions(unparsedSuggestions);
                const [allSuggestions, limitedSuggestions] = filterSortAndDedupeSuggestions(parsedSuggestions, isMobile);
                // We cache the data in two ways: as one big set that we might search
                // and filter, and an exact match for the current query. Mostly this
                // saves time when we have a full limit exact match and
                // don't need to take time searching the larger cache. We also always
                // cache the suggestions in alphabetical order so that we only need
                // to sory by group and not re-sort inside each group.
                queryClient.setQueryData(QueryClientHelper.makeEphemeralQueryKey(KEY), (oldSuggestions = EMPTY_ARRAY) => allSuggestions.length
                    ? uniqBy([...allSuggestions, ...oldSuggestions], 'query').sort((a, b) => a.query.localeCompare(b.query))
                    : oldSuggestions);
                queryClient.setQueryData(QueryClientHelper.makeEphemeralQueryKey(KEY, query), limitedSuggestions);
                // While the query was in flight we may have cleared the query
                if (messageID === messageToTimeMapRef.current.lastMessageID &&
                    isAutosuggestActiveRef.current) {
                    const resolvedSuggestions = maybeSetSuggestions(query, true);
                    onSuggestionsResolvedRef.current?.({ query, suggestions: resolvedSuggestions });
                }
                if (messageToTimeMapRef.current[messageID]) {
                    const latency = performance.now() - (messageToTimeMapRef.current[messageID] ?? 0);
                    latenciesRef.current.push(latency);
                }
            };
            socketRef.current = socket;
        }
        catch (error) {
            // gracefully fail, retries upon next keystroke
            logger.error(CONNECTION_ERROR, error);
        }
    }, [isMobile, maybeSetSuggestions, queryClient]);
    const keepAlive = useCallback(() => {
        if (socketKeepAliveRef.current) {
            clearTimeout(socketKeepAliveRef.current);
        }
        socketKeepAliveRef.current = setTimeout(() => {
            if (socketRef.current) {
                socketRef.current.close();
                socketRef.current = null;
            }
        }, KEEP_ALIVE_TIMEOUT);
    }, []);
    useEffect(() => {
        if (!enabled) {
            return;
        }
        connectWebSocket();
        keepAlive();
        return () => {
            if (socketRef.current) {
                socketRef.current.close();
            }
            if (socketKeepAliveRef.current) {
                clearTimeout(socketKeepAliveRef.current);
            }
        };
    }, [connectWebSocket, enabled, keepAlive]);
    const sendQueryForAutosuggest = useCallback((query, reconnectIfClosed = true) => {
        if (!enabled) {
            isAutosuggestActiveRef.current = false;
            return;
        }
        // If the query is empty, we don't want to send a message
        // or allow an inflight message to update the suggestions
        isAutosuggestActiveRef.current = !!query.trim();
        if (!isAutosuggestActiveRef.current) {
            return;
        }
        // Immediately update if we have cached suggestions--they'll be
        // shown without waiting for the socket connection or round trip
        maybeSetSuggestions(query);
        const openOrConnectingStates = [WebSocket.OPEN, WebSocket.CONNECTING];
        if (!socketRef.current || !openOrConnectingStates.includes(socketRef.current.readyState)) {
            if (reconnectIfClosed) {
                connectWebSocket(() => {
                    sendQueryForAutosuggest(query, false);
                });
            }
            return;
        }
        else {
            keepAlive();
        }
        const messageID = messageToTimeMapRef.current.lastMessageID + 1;
        const stringMessageID = messageID.toString();
        messageToTimeMapRef.current.lastMessageID = messageID;
        try {
            const message = JSON.stringify({
                q: query,
                uuid: stringMessageID,
                full_completion: true,
            });
            if (socketRef.current.readyState === WebSocket.CONNECTING) {
                return;
            }
            socketRef.current.send(message);
            messageToTimeMapRef.current[messageID] = performance.now();
        }
        catch (error) {
            // gracefully fail, retries upon next keystroke
            logger.error(SEND_QUERY_ERROR, error);
        }
    }, [connectWebSocket, enabled, keepAlive, maybeSetSuggestions]);
    useEffect(() => {
        const interval = setInterval(() => {
            if (latenciesRef.current.length === 0)
                return;
            reportAutosuggestLatencies({
                latencies: latenciesRef.current,
                isMobile,
            });
            latenciesRef.current = [];
        }, REPORT_LATENCY_DELAY);
        return () => clearInterval(interval);
    }, [isMobile]);
    return useMemo(() => ({
        getCachedSuggestions,
        handleUpdateAutosuggestions: sendQueryForAutosuggest,
    }), [getCachedSuggestions, sendQueryForAutosuggest]);
};
//# sourceMappingURL=useAutosuggest.js.map